import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Button } from 'antd';
import { ChevronRight, FilePlus2, Home, Menu, Search } from 'lucide-react';

import { ROUTE_URLS } from '../../routes';
import { canSeePage } from '../../navigation';
import { useI18n } from '../../i18n';
import { ThemeSwitcher } from './ThemeSwitcher';
import { LanguageSwitcher } from './LanguageSwitcher';
import { Notifications } from './Notifications';
import { UserMenu } from './UserMenu';
import { CommandPalette } from './CommandPalette';

const Breadcrumbs = ({ crumbs = [] }) => {
  const { t } = useI18n();
  return (
    <nav className='wm-breadcrumb' aria-label={t('header.breadcrumb')}>
      <Link to={ROUTE_URLS.HOME} aria-label={t('nav.home')} className='wm-breadcrumb__item wm-hide-md'>
        <Home size={15} strokeWidth={1.75} />
      </Link>
      {crumbs.map((item, i) => {
        const last = i === crumbs.length - 1;
        return (
          <React.Fragment key={item.key}>
            <ChevronRight size={14} className='wm-breadcrumb__sep' />
            {item.to && !last ? (
              <Link to={item.to} className='wm-breadcrumb__item'>
                {t(item.key)}
              </Link>
            ) : (
              <span
                className={`wm-breadcrumb__item${last ? ' is-current is-last' : ''}`}
                aria-current={last ? 'page' : undefined}
              >
                {t(item.key)}
              </span>
            )}
          </React.Fragment>
        );
      })}
    </nav>
  );
};

export const Header = ({ onOpenMobileNav, crumbs }) => {
  const { t } = useI18n();
  const { pathname } = useLocation();
  const { isAdmin, pages } = useSelector(state => state.authReducer);
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => {
    const onKey = e => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearchOpen(open => !open);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <header className='wm-header'>
      <div className='wm-header__left'>
        <button
          type='button'
          className='wm-icon-btn wm-header__mobile-toggle'
          onClick={onOpenMobileNav}
          aria-label={t('nav.openMenu')}
        >
          <Menu size={19} strokeWidth={1.75} />
        </button>
        <Breadcrumbs crumbs={crumbs} />
      </div>
      <div className='wm-header__right'>
        <button
          type='button'
          className='wm-search-trigger'
          onClick={() => setSearchOpen(true)}
          aria-label={t('common.search')}
        >
          <Search size={15} strokeWidth={1.75} />
          <span>{t('header.search')}</span>
          <kbd>{t('header.searchShortcut')}</kbd>
        </button>
        {pathname !== ROUTE_URLS.CREATE_REPORT && canSeePage(isAdmin, pages, 'createReport') && (
          <Link to={ROUTE_URLS.CREATE_REPORT} className='wm-hide-md'>
            <Button type='primary' icon={<FilePlus2 size={15} strokeWidth={2} />}>
              <span className='wm-hide-lg-text'>{t('nav.createReport')}</span>
            </Button>
          </Link>
        )}
        <Notifications />
        <ThemeSwitcher />
        <LanguageSwitcher />
        <UserMenu />
      </div>
      <CommandPalette open={searchOpen} onClose={() => setSearchOpen(false)} />
    </header>
  );
};
